import React from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, MarkPointComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import Badge from '../../../../components/base/Badge';

echarts.use([TooltipComponent, GridComponent, MarkPointComponent, BarChart, CanvasRenderer]);

interface ChartDataItem {
  name: string;
  value: number;
}

interface PromptInjectionChartProps {
  data: ChartDataItem[];
  title: string;
  description: string;
}

const PromptInjectionChart: React.FC<PromptInjectionChartProps> = ({ data, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Ensure we have valid colors with fallbacks
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';
  const dangerColor = getThemeColor('danger') || '#dc3545';

  const attributes = data.map(item => item.name);
  const values = data.map(item => item.value);

  const zeroValuePoints = data
    .map((item, index) =>
      item.value === 0
        ? {
            name: item.name,
            coord: [index, 0],
            symbol: 'circle',
            symbolSize: 10,
            itemStyle: {
              color: dangerColor // Use a color that contrasts the bar color
            },
            label: { show: false }
          }
        : null
    )
    .filter(Boolean) as any[];

  const options = {
    color: primaryColor,
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>${param.name}:</strong> ${param.value}`;
      }
    },
    xAxis: {
      type: 'category',
      data: attributes,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor, rotate: 45 }
    },
    yAxis: {
      type: 'value',
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor }
    },
    series: [
      {
        type: 'bar',
        data: values,
        barWidth: '50%',
        itemStyle: {
          borderRadius: [5, 5, 0, 0],
          color: primaryColor
        },
        markPoint: {
          symbolOffset: [0, -10],
          data: zeroValuePoints
        }
      }
    ],
    grid: { left: '10%', right: '10%', bottom: '15%', top: '15%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">
              {title}
              <Badge bg="warning" variant="phoenix" pill className="ms-2">
                -6.8%
              </Badge>
            </h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default PromptInjectionChart;
